import { useMutationWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { UseMutationOptions, useQueryClient } from 'react-query';
import http from '../config/http-service';
import { restaurantInquiriesEndpoint } from './use-create-restaurant-inquiry-api';

// DELETE .../items/:itemId/quotes/:quoteId — remove ONE quote outright
// (verified against local user-mngt-srv 2026-07-07; a pre-update UMS returns
// 404 for this route, which surfaces as the error toast). Deleting is for
// correcting mistakes; declining a supplier's offer stays a REJECT so the
// decision is audited.
type TData = { id: number };
type TError = AxiosError;
type TVariables = { id: number | string; itemId: number; quoteId: number };

type Options = Omit<UseMutationOptions<TData, TError, TVariables>, 'mutationFn'>;
type TReturnType = ReturnType<typeof useMutationWrapper<TData, TError, TVariables>>;

export function useDeleteInquiryItemQuoteApi(options?: Options): TReturnType {
  const queryClient = useQueryClient();
  const { onSuccess: userOnSuccess, ...restOptions } = options || {};

  const mutationFn = async ({ id, itemId, quoteId }: TVariables): Promise<TData> => {
    const response = await http.delete(`${restaurantInquiriesEndpoint}/${id}/items/${itemId}/quotes/${quoteId}`);
    return response.data?.data ?? response.data;
  };

  return useMutationWrapper<TData, TError, TVariables>(mutationFn, {
    mutationKey: [restaurantInquiriesEndpoint, 'delete-quote'],
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries([restaurantInquiriesEndpoint]);
      userOnSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
}
